import { useCallback, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { getUser } from '@frontend/auth-helpers';
import { useAppFlagStore } from '@frontend/shared';
import { pendo } from '@frontend/tracking';
import { useComponentEngagementStore } from '../../common/store/component-engagement-store';
import { TSS_IN_APP_BANNER_BETA_VARIANT_FF, TSS_IN_APP_BANNER_FF } from '../constants';
import { getInAppAwarenessBannerTrackingId } from '../tracking-ids';
import { InAppAwarenessPromoSource } from '../types';

interface UseCallIntelInAppBannerVisibilityProps {
  locationIds: string[];
  isDemo?: boolean;
  componentId: InAppAwarenessPromoSource;
  enabled: boolean;
  maxVisibilityCount?: number;
  maxInteractionCount?: number;
  bannerFF?: string;
  betaBannerFF?: string;
}
export const useCallIntelInAppBannerVisibility = ({
  locationIds,
  isDemo,
  componentId,
  enabled = true,
  maxVisibilityCount = 1,
  maxInteractionCount = 0,
  bannerFF = TSS_IN_APP_BANNER_FF,
  betaBannerFF = TSS_IN_APP_BANNER_BETA_VARIANT_FF,
}: UseCallIntelInAppBannerVisibilityProps) => {
  const { getFeatureFlagValue } = useAppFlagStore();
  const user = getUser();

  const isBannerEnabled = getFeatureFlagValue({ locationIds, flagName: bannerFF });
  const isBetaBanner = getFeatureFlagValue({ locationIds, flagName: betaBannerFF });

  const { ref, inView } = useInView({
    triggerOnce: true,
  });

  const componentTrackingId = useMemo(
    () => [componentId, user?.username, isBetaBanner ? 'beta' : ''].filter(Boolean).join(':'),
    [componentId, isBetaBanner, user?.username]
  );

  const { getEngagementData, trackInteraction, trackVisibility } = useComponentEngagementStore();

  const { visibilityCount, interactionCount } = getEngagementData({
    componentId: componentTrackingId,
  });

  const shouldShowBanner = useMemo(() => {
    if (isDemo) {
      return false;
    }
    if (!isBannerEnabled) {
      return false;
    }
    if (interactionCount > maxInteractionCount) {
      return false;
    }
    if (visibilityCount > maxVisibilityCount) {
      return false;
    }

    return enabled;
  }, [isDemo, isBannerEnabled, interactionCount, maxInteractionCount, visibilityCount, maxVisibilityCount, enabled]);

  const handleDismiss = useCallback(() => {
    trackInteraction({
      componentId: componentTrackingId,
    });
  }, [componentTrackingId, trackInteraction]);

  useEffect(() => {
    if (inView && shouldShowBanner) {
      trackVisibility({
        componentId: componentTrackingId,
      });

      pendo.track(
        getInAppAwarenessBannerTrackingId({
          isBeta: isBetaBanner,
          source: componentId,
          action: 'visible',
        }),
        {
          locationIds,
          componentId,
          isBeta: isBetaBanner,
        }
      );
    }
  }, [inView, isBetaBanner, trackVisibility, shouldShowBanner, componentTrackingId, componentId, locationIds]);

  return {
    handleDismiss,
    ref,
    shouldShowBanner,
    isBetaBanner,
  };
};
